import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import GetPetVetLogo from "@/components/brand/GetPetVetLogo";
import { useFooterLogoHeight } from "@/contexts/LogoSizeContext";



interface FooterArticle {
  id: string;
  slug: string;
  title: string;
}

const Footer = () => {
  const { role } = useUserRole();
  const footerLogoH = useFooterLogoHeight();
  const [latestNews, setLatestNews] = useState<FooterArticle[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("news_articles")
        .select("id, slug, title")
        .order("published_at", { ascending: false })
        .limit(4);
      if (!cancelled) setLatestNews((data ?? []) as FooterArticle[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <footer className="border-t border-border bg-muted/30 mt-auto">
      <div className="max-w-6xl mx-auto px-4 pt-10 pb-28 sm:pb-10">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-x-8 gap-y-10">
          {/* Brand */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-2 flex flex-col items-start max-w-sm">
            <Link
              to="/"
              className="inline-flex items-center mb-5 max-w-full"
              aria-label="GetPetVet home - The best care yet"
            >
              <span className="sr-only">GetPetVet</span>
              <GetPetVetLogo
                aria-hidden="true"
                style={{ height: footerLogoH, width: footerLogoH * (2054 / 496), aspectRatio: "2054 / 496" }}
                className="block max-w-full h-auto text-foreground"
              />
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed max-w-[280px]">
              Connecting UK pet owners with trusted local vets. Transparent pricing, instant booking.
            </p>
          </div>

          {/* Pet Owners */}
          <div>
            <h4 className="font-semibold text-foreground mb-3 text-sm">Pet Owners</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="text-muted-foreground hover:text-primary transition-colors">Find a Vet</Link></li>
              <li><Link to="/book-appointment" className="text-muted-foreground hover:text-primary transition-colors">Book Appointment</Link></li>
              <li><Link to="/my-dashboard" className="text-muted-foreground hover:text-primary transition-colors">My Dashboard</Link></li>
              <li><Link to="/signup" className="text-muted-foreground hover:text-primary transition-colors">Get Started</Link></li>
            </ul>
          </div>

          {/* Vet Practices */}
          <div>
            <h4 className="font-semibold text-foreground mb-3 text-sm"><Link to="/for-vets" className="hover:text-primary transition-colors">Vet Practices</Link></h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/for-vets" className="text-muted-foreground hover:text-primary transition-colors">For Vets</Link></li>
              <li><Link to="/vet-signup" className="text-muted-foreground hover:text-primary transition-colors">Register Your Practice</Link></li>
              {role === "vet" && (
                <li><Link to="/vet-dashboard" className="text-muted-foreground hover:text-primary transition-colors">Vet Dashboard</Link></li>
              )}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold text-foreground mb-3 text-sm">Company</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">About Us</Link></li>
              <li><Link to="/accessibility" className="text-muted-foreground hover:text-primary transition-colors">Accessibility</Link></li>
              <li><Link to="/blog" className="text-muted-foreground hover:text-primary transition-colors">Blog</Link></li>
              <li><Link to="/cma" className="text-muted-foreground hover:text-primary transition-colors">CMA & Transparency</Link></li>
              <li><Link to="/contact-us" className="text-muted-foreground hover:text-primary transition-colors">Contact Us</Link></li>
              <li><Link to="/helpdesk" className="text-muted-foreground hover:text-primary transition-colors">Helpdesk</Link></li>
              <li><Link to="/how-it-works" className="text-muted-foreground hover:text-primary transition-colors">How It Works</Link></li>
              <li><Link to="/press" className="text-muted-foreground hover:text-primary transition-colors">Press</Link></li>
              <li><Link to="/security" className="text-muted-foreground hover:text-primary transition-colors">Security</Link></li>
              <li><Link to="/status" className="text-muted-foreground hover:text-primary transition-colors">Status</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold text-foreground mb-3 text-sm">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/anti-slavery-policy" className="text-muted-foreground hover:text-primary transition-colors">Anti-Slavery Policy</Link></li>
              <li><Link to="/code-of-conduct" className="text-muted-foreground hover:text-primary transition-colors">Code of Conduct</Link></li>
              <li><Link to="/cookies" className="text-muted-foreground hover:text-primary transition-colors">Cookie Policy</Link></li>
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent("gpv:consent-reopen"))}
                  className="text-muted-foreground hover:text-primary transition-colors text-left"
                >
                  Cookie settings
                </button>
              </li>
              <li><Link to="/no-abuse-policy" className="text-muted-foreground hover:text-primary transition-colors">No Abuse Policy</Link></li>
              <li><Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">Terms & Conditions</Link></li>
            </ul>
          </div>

          {/* Latest News */}
          <div className="col-span-2 sm:col-span-3 lg:col-span-1">
            <h4 className="font-semibold text-foreground mb-3 text-sm">
              <Link to="/news" className="hover:text-primary transition-colors">
                Latest News
              </Link>
            </h4>
            <ul className="space-y-2 text-sm">
              {latestNews.length === 0 ? (
                <li className="text-muted-foreground text-xs">
                  Fresh UK pet & vet stories - coming soon.
                </li>
              ) : (
                latestNews.map((a) => (
                  <li key={a.id}>
                    <Link
                      to={`/news/${a.slug}`}
                      className="text-muted-foreground hover:text-primary transition-colors line-clamp-2"
                    >
                      {a.title}
                    </Link>
                  </li>
                ))
              )}
              <li className="pt-1">
                <Link
                  to="/news"
                  className="text-primary hover:underline text-xs font-medium"
                >
                  See all news →
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border mt-8 pt-6 flex flex-col items-center justify-center gap-4">
          <div className="flex items-center gap-2">
            <a
              href="https://x.com/getpetvet"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GetPetVet on X (Twitter)"
              className="w-9 h-9 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 35 35" fill="none">
                <path d="M19.0154 15.5055L26.6401 6.82617H24.8339L18.2106 14.3608L12.9245 6.82617H6.82605L14.8214 18.221L6.82605 27.3215H8.63223L15.6221 19.363L21.2059 27.3215H27.3043M9.28412 8.15999H12.0589L24.8326 26.0532H22.0571" fill="currentColor" />
              </svg>
            </a>
            <a
              href="https://www.facebook.com/getpetvet"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GetPetVet on Facebook"
              className="w-9 h-9 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06C2 17.08 5.66 21.24 10.44 22v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.89 3.77-3.89 1.09 0 2.24.19 2.24.19v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.77l-.44 2.91h-2.33V22C18.34 21.24 22 17.08 22 12.06Z" />
              </svg>
            </a>
            <a
              href="https://www.tiktok.com/@getpetvet"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GetPetVet on TikTok"
              className="w-9 h-9 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 35 35" fill="none">
                <path d="M25.8253 15.307C24.0654 15.307 22.3456 14.747 20.9057 13.7471V20.8665C20.9057 22.2264 20.5057 23.5063 19.7458 24.5862C18.9859 25.6661 17.906 26.506 16.6661 26.986C15.4262 27.426 14.0663 27.5059 12.7864 27.186C11.5065 26.866 10.3466 26.1461 9.50667 25.1461C8.62675 24.1462 8.06679 22.9063 7.9468 21.5864C7.78682 20.2666 8.0268 18.9467 8.66674 17.7868C9.30669 16.6269 10.2666 15.667 11.4665 15.067C12.6264 14.4671 13.9863 14.2671 15.2662 14.4271V18.0267C14.6662 17.8668 14.0263 17.8668 13.4263 18.0667C12.8664 18.2667 12.3464 18.6267 11.9465 19.1467C11.5865 19.6666 11.3865 20.2666 11.4265 20.9065C11.4265 21.5465 11.6265 22.1464 11.9865 22.6664C12.3464 23.1463 12.8664 23.5463 13.4663 23.7063C14.0663 23.9063 14.7462 23.9063 15.3062 23.7063C15.9061 23.5063 16.4261 23.1463 16.826 22.6264C17.186 22.1464 17.386 21.5065 17.386 20.8665V6.90769H20.9057C20.9057 7.18767 20.9057 7.50764 20.9857 7.78762C21.1057 8.46756 21.3457 9.06751 21.7056 9.62746C22.1056 10.1874 22.5856 10.6674 23.1455 11.0273C23.9454 11.5473 24.8654 11.8273 25.8253 11.8273V15.307Z" fill="currentColor" />
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@GetPetVetUK"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="GetPetVet on YouTube"
              className="w-9 h-9 inline-flex items-center justify-center rounded-full text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 35 35" fill="none">
                <path d="M27.3052 11.8673C27.7451 13.5871 27.7451 17.1468 27.7451 17.1468C27.7451 17.1468 27.7451 20.7465 27.3052 22.4664C27.0652 23.3863 26.3052 24.1062 25.3853 24.3862C23.6655 24.8262 16.8261 24.8262 16.8261 24.8262C16.8261 24.8262 9.98663 24.8262 8.30677 24.3862C7.34686 24.1062 6.62692 23.3863 6.34694 22.4664C5.90698 20.7465 5.90698 17.1468 5.90698 17.1468C5.90698 17.1468 5.90698 13.5871 6.34694 11.8673C6.62692 10.9074 7.34686 10.1874 8.30677 9.94744C9.98663 9.46748 16.8261 9.46748 16.8261 9.46748C16.8261 9.46748 23.6655 9.46748 25.3453 9.94744C26.3052 10.1874 27.0652 10.9074 27.3052 11.8673ZM14.5862 20.4265L20.3058 17.1468L14.5862 13.9071V20.4265Z" fill="currentColor" />
              </svg>
            </a>
          </div>
          <p className="text-xs text-muted-foreground text-center max-w-2xl">
            © {new Date().getFullYear()} GetPetVet Limited. All rights reserved.
            <br />
            GetPetVet™, Symptom Sniffer™, Symptom Sniffer Pro™ and GetVideoVet™ are trademarks of GetPetVet Limited.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
